// src/InvoicePDF.js
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const fmt = (amount) => {
  const num = Math.round((amount || 0) * 100) / 100;
  const [intPart, decPart] = num.toFixed(2).split('.');
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ',' + decPart;
};
const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const pdfSafe = (str) => {
  if (!str) return '';
  return str
    .replace(/é/g, String.fromCharCode(233))
    .replace(/è/g, String.fromCharCode(232))
    .replace(/ê/g, String.fromCharCode(234))
    .replace(/à/g, String.fromCharCode(224))
    .replace(/â/g, String.fromCharCode(226))
    .replace(/ç/g, String.fromCharCode(231))
    .replace(/ô/g, String.fromCharCode(244))
    .replace(/ù/g, String.fromCharCode(249))
    .replace(/û/g, String.fromCharCode(251))
    .replace(/î/g, String.fromCharCode(238))
    .replace(/É/g, String.fromCharCode(201))
    .replace(/À/g, String.fromCharCode(192))
    .replace(/’/g, "'")
    .replace(/–/g, '-')
    .replace(/—/g, '-')
    .replace(/á/g, String.fromCharCode(225))
    .replace(/í/g, String.fromCharCode(237));
};

const TYPE_LABELS = {
  acompte: "Facture d'Acompte",
  solde: 'Facture de Solde',
  comptant: 'Facture 100% Comptant',
  echeance: 'Facture — Échéance',
};

const INVOICE_LOGO_URL = 'https://devis.takoussane.com/logo-takoussane.png';
const TAMPON_PAYE_URL = 'https://devis.takoussane.com/images/tampon-paye.png';

export const buildInvoicePDF = (invoice, quote, _logoB64) => {
  const logoB64 = INVOICE_LOGO_URL;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, H = 297;
  const M = 14, ML = 14, MR = 14;

  // ── Couleurs ────────────────────────────────────────────────────────────────
  const BLUE       = [0, 82, 155];
  const ORANGE     = [232, 67, 26];
  const LIGHT_BLUE = [232, 241, 255];
  const GRAY       = [130, 130, 130];
  const DARK_BLUE  = [0, 82, 155];
  const LINE       = [200, 200, 200];
  const LIGHT_GRAY = [248, 250, 252];
  const WHITE      = [255, 255, 255];

  // ── Header bleu premium ──────────────────────────────────────────────────────
  const HEADER_H = 31;
  const STRIPE = 1.2;
  doc.setFillColor(...BLUE);
  doc.rect(0, 0, W, HEADER_H, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, HEADER_H - STRIPE, W, STRIPE, 'F');

  // Rectangle glassmorphism derrière le logo
  const logoW = 72, logoH = 13, logoX = M;
  const logoY = (HEADER_H - STRIPE - logoH) / 2;
  const padX = 3, padY = 2.5;
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.15 }));
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(logoX - padX, logoY - padY, logoW + padX * 2, logoH + padY * 2, 3, 3, 'F');
  doc.restoreGraphicsState();
  try { doc.addImage(INVOICE_LOGO_URL, 'PNG', logoX, logoY, logoW, logoH); } catch (_) {}

  // Titre FACTURE à droite
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', W - M, 12, { align: 'right' });

  const typeLabel = TYPE_LABELS[invoice.type] || 'Facture';
  const echeanceLabel = invoice.type === 'echeance'
    ? ` ${invoice.echeance_numero - 1}/${invoice.echeance_total - 1}`
    : '';

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`${typeLabel}${echeanceLabel}`, W - M, 18.5, { align: 'right' });
  doc.text(`N° ${invoice.invoice_number}`, W - M, 23.5, { align: 'right' });
  doc.text(
    invoice.date_emission ? `Emise le ${fmtDate(invoice.date_emission)}` : 'Non emise',
    W - M, HEADER_H - STRIPE - 1.5, { align: 'right' }
  );

  // Tampon PAYÉE
  // Tampon deplace sous le tableau (voir plus bas)

  doc.setTextColor(0, 0, 0);

  // ── Référence devis ──────────────────────────────────────────────────────────
  let y = HEADER_H + 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GRAY);
  doc.text(`Ref. Devis : ${quote.quote_number} du ${fmtDate(quote.date)}`, ML, y);
  doc.setTextColor(0, 0, 0);

  // ── Blocs ÉMETTEUR / CLIENT ──────────────────────────────────────────────────
  y += 5;
  const colW = (W - ML - MR - 6) / 2;
  doc.setFillColor(...LIGHT_BLUE);
  doc.rect(ML, y, colW, 38, 'F');
  doc.rect(ML + colW + 6, y, colW, 38, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLUE);
  doc.text('EMETTEUR', ML + 3, y + 6);
  doc.text('CLIENT', ML + colW + 9, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  const company = quote.company_info || {};
  const client = quote.client_info || {};
  const clientTitle = client.attentionTitle || '';
  const clientAttn = client.attentionName || '';
  const clientName = client.clientName || client.name || '';
  const clientFullName = clientName || (clientTitle && clientAttn ? `${clientTitle} ${clientAttn}` : '');

  [
    'TAKOUSSANE ENERGY',
    company.address || 'BP 302 Rufisque Ouest, Dakar',
    `Tel: ${company.tel || '(+221) 33 836 67 83'}`,
    `Mob: ${company.mob || '(+221) 78 346 88 51'}`,
    `Email: ${company.email || 'contact@takoussane.com'}`,
    company.contact ? `Contact: ${company.contact}` : '',
  ].filter(Boolean).forEach((l, i) => doc.text(l, ML + 3, y + 13 + i * 4));

  [
    clientFullName,
    `Mob: ${client.mobile || client.mob || '(non renseigne)'}`,
    `Tel: ${client.phone || '(non renseigne)'}`,
    `Email: ${client.email || '(non renseigne)'}`,
    `Ville: ${client.address || '(non renseigne)'}`,
    `Pays: ${client.region || 'Senegal'}`,
  ].forEach((l, i) => { if (l) doc.text(l, ML + colW + 9, y + 13 + i * 4); });

  // ── Titre du kit (pour le tableau) ──────────────────────────────────────────
  const kitTitre = (quote.items?.find(i => i._titre)?.description) ||
    (quote.items?.filter(i => !i._titre)?.[0]?.description
      ? 'Réf. devis : ' + (quote.quote_number || '')
      : '');

   const garanties = 'Garanties : Syst' + String.fromCharCode(232) + 'me complet 2 ans - Panneaux solaires 12 ans\nRendement 86.4% ' + String.fromCharCode(224) + ' 30 ans - D' + String.fromCharCode(233) + 'gradation 0.40%/an.';

  // ── Tableau montants ─────────────────────────────────────────────────────────
  y += 44;
  doc.autoTable({
    startY: y,
     head: [[{content:'Description'}, {content:'Montant FCFA', styles:{halign:'right'}}]],
    body: [
      [
        invoice.type === 'acompte'
          ? 'Acompte à la commande (50% du total TTC)\n\n' + kitTitre + (kitTitre ? '\n\n' : '') + garanties
          : invoice.pourcentage === 100 && invoice.echeance_total === 1
          ? 'Paiement intégral 100% à la commande\n\n' + kitTitre + (kitTitre ? '\n\n' : '') + garanties
          : invoice.type === 'solde'
          ? 'Solde à la mise en service (50% du total TTC)\n\n' + kitTitre + (kitTitre ? '\n\n' : '') + garanties
          : 'Echeance ' + (invoice.echeance_numero - 1) + '/' + (invoice.echeance_total - 1) + ' du solde\n\n' + kitTitre + (kitTitre ? '\n\n' : '') + garanties,
        fmt(invoice.montant)
      ],
    ],
    foot: [
       [{content:'Montant Total', styles:{halign:'left'}}, {content:fmt(invoice.montant) + ' FCFA', styles:{halign:'right'}}],
    ],
    headStyles: {
      fillColor: DARK_BLUE, textColor: WHITE,
      fontStyle: 'bold', fontSize: 9
    },
    footStyles: {
      fillColor: DARK_BLUE, textColor: WHITE,
      fillColor: DARK_BLUE, textColor: WHITE, fontStyle: 'bold', fontSize: 10
    },
    bodyStyles: { fontSize: 9 },
    tableWidth: 182,
    columnStyles: {
      0: { cellWidth: 137 },
      1: { cellWidth: 45, halign: 'right' }
    },
    margin: { left: ML, right: MR },
    theme: 'striped',
  });

  y = doc.lastAutoTable.finalY + 8;

  // ── Récap devis ───────────────────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...GRAY);
  if (invoice.pourcentage !== 100 || invoice.echeance_total !== 1) {
    doc.text(
      'Rappel : Total devis TTC = ' + fmt(quote.total_with_vat) + ' FCFA' +
      ' - Acompte = ' + fmt(quote.total_with_vat * 0.5) + ' FCFA' +
      ' - Solde = ' + fmt(quote.total_with_vat * 0.5) + ' FCFA',
      ML, y
    );
  }
  // ── Conditions de paiement ────────────────────────────────────────────────────
  y += 8;
  doc.setFillColor(...LIGHT_GRAY);
  doc.rect(ML, y, W - ML - MR, 14, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...DARK_BLUE);
  doc.text(pdfSafe('Conditions de paiement :'), ML + 3, y + 5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(invoice.pourcentage === 100 && invoice.echeance_total === 1 ? '100% à la commande.' : '50% à la commande, solde à la mise en service du système.', ML + 3, y + 10);

  // ── Footer ────────────────────────────────────────────────────────────────────
  // Tampon PAYE (image) positionne a droite sous le tableau
  if (invoice.statut === 'payee') {
    try {
      const tampW = 45, tampH = 36;
      const tampX = W - MR - tampW - 5;
      const tampY = y + 14;
      doc.addImage(TAMPON_PAYE_URL, 'PNG', tampX, tampY, tampW, tampH);
    } catch (_) {}
  }

  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.5);
  doc.line(ML, H - 22, W - MR, H - 22);
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  const footer = 'TAKOUSSANE ENERGY | BP 302 Rufisque Ouest, Dakar - Senegal | RC: SN DKR 2016 M 1436 | NINEA: 005711090';
  doc.text(footer, W / 2, H - 17, { align: 'center' });
  doc.text('Page 1 / 1', W - MR, H - 17, { align: 'right' });

  return doc;
};
